'use client';

import React, { useCallback, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

interface CarouselPostProps {
  images: string[];
  title?: string;
  onImagesChange?: (newImages: string[]) => void;
  type: 'linkedin' | 'instagram';
}

interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

export default function CarouselPost({
  images: initialImages,
  title,
  onImagesChange,
  type,
}: CarouselPostProps) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>(
    initialImages?.map((image) => ({
      url: image?.url || image,
      type: (image?.url || image)?.endsWith('.mp4') ? 'video' : 'image',
    })),
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const isLinkedIn = type === 'linkedin';

  const goToNext = useCallback(
    (e) => {
      e.stopPropagation();
      setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
    },
    [mediaItems.length],
  );

  const goToPrevious = useCallback(
    (e) => {
      e.stopPropagation();
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + mediaItems.length) % mediaItems.length,
      );
    },
    [mediaItems.length],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newMediaUrl = URL.createObjectURL(file);
      const newMediaType = file.type.startsWith('video/') ? 'video' : 'image';

      setMediaItems((prevItems) => {
        const newItems = [...prevItems];
        newItems[currentIndex] = { url: newMediaUrl, type: newMediaType };
        if (onImagesChange) {
          onImagesChange(newItems.map((item) => item.url));
        }
        return newItems;
      });
    },
    [currentIndex, onImagesChange],
  );

  const accept = isLinkedIn
    ? { 'image/*': [] }
    : { 'image/*': [], 'video/*': [] };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
  });

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  return (
    <div className="group relative aspect-square h-fit min-h-[400px] w-full border">
      {mediaItems.map((item, index) => (
        <div
          key={index}
          className={`absolute left-0 top-0 h-full w-full transition-opacity duration-300 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
          {...getRootProps()}
        >
          {item.type === 'video' ? (
            <video
              src={item.url}
              className="h-full w-full object-cover"
              autoPlay
            />
          ) : (
            <img
              src={item.url}
              alt={`Slide ${index + 1}`}
              type="image/png"
              className="h-full w-full object-cover"
            />
          )}
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
              <p className="text-center text-white">
                Drop the image or video here to replace
              </p>
            </div>
          )}
        </div>
      ))}

      {isLinkedIn && currentIndex === 0 && title && (
        <div className="absolute left-0 right-0 top-0 flex items-center justify-between bg-black/50 px-4 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <span className="text-sm text-white">
            {`${mediaItems.length} pages`}
          </span>
        </div>
      )}

      <div className="absolute left-4 right-4 top-1/2 flex items-center justify-between opacity-0 transition-opacity group-hover:opacity-100">
        {currentIndex === 0 ? (
          <div></div>
        ) : (
          <button
            onClick={goToPrevious}
            className={`${isLinkedIn ? 'bg-black/50' : 'bg-white'} text-${isLinkedIn ? 'white' : 'black'} rounded-full p-2`}
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {currentIndex === mediaItems.length - 1 ? (
          <div></div>
        ) : (
          <button
            onClick={goToNext}
            className={`${isLinkedIn ? 'bg-black/50' : 'bg-white'} text-${isLinkedIn ? 'white' : 'black'} rounded-full p-2`}
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {isLinkedIn ? (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-black/50 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <div className="w-10 text-xs text-white">{`${currentIndex + 1} / ${mediaItems.length}`}</div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-black/50">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{
                width: `${((currentIndex + 1) / mediaItems.length) * 100}%`,
              }}
            />
          </div>
        </div>
      ) : (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`h-2 w-2 rounded-full ${
                index === currentIndex ? 'bg-blue-500' : 'bg-gray-300'
              } cursor-pointer focus:outline-none`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
